import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import {
  installNoticeVisible,
  markNoticesSeen,
  noticeBadgeCount,
  subscribeAdminNotices,
  unseenAdminNotices,
} from './notices';

/** Bell notices for the signed-in session. Closing the panel marks them seen. */
export default function useAppNotices() {
  const { currentUser, userProfile } = useAuth();
  const { reportError } = useErrorReport();
  const [adminNotices, setAdminNotices] = useState([]);
  const uid = currentUser?.uid || '';

  useEffect(() => {
    if (!uid) {
      setAdminNotices([]);
      return undefined;
    }
    return subscribeAdminNotices(setAdminNotices, (err) => {
      reportError(err, {
        pageLabel: 'Header',
        source: 'admin-notices-subscribe',
        userMessage: 'Could not load notifications.',
      });
    });
  }, [uid, reportError]);

  const notices = useMemo(
    () => unseenAdminNotices(adminNotices, userProfile?.seenNoticeIds),
    [adminNotices, userProfile?.seenNoticeIds]
  );
  const showInstallNotice = installNoticeVisible(userProfile);
  const noticeCount = noticeBadgeCount(userProfile, adminNotices);

  const dismiss = useCallback(async () => {
    if (!uid) return;
    const noticeIds = notices.map((notice) => notice.id);
    if (!showInstallNotice && noticeIds.length === 0) return;
    try {
      await markNoticesSeen(uid, { noticeIds, clearInstall: showInstallNotice });
    } catch (err) {
      reportError(err, {
        pageLabel: 'Header',
        source: 'notices-seen',
        userMessage: 'Could not update notifications.',
      });
    }
  }, [uid, notices, showInstallNotice, reportError]);

  return { notices, showInstallNotice, noticeCount, dismiss };
}
